import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const NAMES = ['María G.', 'Carlos M.', 'Ana L.', 'José R.', 'Laura P.', 'Miguel A.', 'Patricia S.', 'Roberto D.', 'Carmen V.', 'Fernando T.', 'Isabel N.', 'Andrés H.', 'Sofía B.', 'Diego C.', 'Valentina F.', 'Gabriel O.', 'Daniela E.', 'Alejandro K.', 'Lucía W.', 'Martín J.', 'Elena Q.', 'Ricardo Z.', 'Camila U.', 'Sebastián I.', 'Paula Y.', 'Tomás X.', 'Natalia R.', 'Emilio P.', 'Andrea M.', 'Javier L.'];
const CITIES = ['Miami, FL', 'Houston, TX', 'Los Angeles, CA', 'New York, NY', 'Chicago, IL', 'San Antonio, TX', 'Phoenix, AZ', 'Dallas, TX', 'San Diego, CA', 'Denver, CO', 'Orlando, FL', 'Atlanta, GA', 'Las Vegas, NV', 'Austin, TX', 'Tampa, FL', 'Charlotte, NC', 'Sacramento, CA', 'Portland, OR', 'San Jose, CA', 'Albuquerque, NM'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'El Protocolo de la Mañana',
  editionLabel: 'Pack Despertar',
  priceNumber: 27,
  priceText: '$27',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ph3TqvShZoWL1GB8WikDyW/hf_20260319_213435_88d14265-cbf1-4ff8-960c-f0f0c4ea3116.jpg'
}, {
  title: 'El Protocolo de la Mañana',
  editionLabel: 'Edición Digital',
  priceNumber: 17,
  priceText: '$17',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/bK1ZdzYg9Ntey2cNnEvmPK/hf_20260319_213341_0b100751-983e-4e2e-93a4-26990d8799b9.png'
}];
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Strategy 1: JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.category || 'Digital',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price}`,
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // Strategy 2: Scan DOM for price patterns and nearby product info
    const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g;
    const allText = document.body.innerText || '';
    const priceMatches: {
      value: number;
      text: string;
    }[] = [];
    let match: RegExpExecArray | null;
    while ((match = priceRegex.exec(allText)) !== null) {
      const val = parseFloat(match[1]);
      // Filter out obviously non-product prices (very high strikethrough values)
      if (val > 0 && val < 100) {
        priceMatches.push({
          value: val,
          text: match[0]
        });
      }
    }
    // Look for the main product image
    const heroImg = document.querySelector('img[alt*="Protocolo"]') as HTMLImageElement;
    const bundleImg = document.querySelector('img[alt*="Colección"]') as HTMLImageElement;
    const mainThumb = bundleImg?.src || heroImg?.src || '';
    // Look for the main product title from h1
    const h1 = document.querySelector('h1');
    const productTitle = h1?.textContent?.split('—')[0]?.trim()?.substring(0, 60) || 'El Protocolo de la Mañana';
    // Find the actual selling price (smallest non-trivial price, likely the offer price)
    const uniquePrices = [...new Set(priceMatches.map(p => p.value))].sort((a, b) => a - b);
    if (uniquePrices.length > 0) {
      const mainPrice = uniquePrices[0]; // Lowest price = the actual offer
      products.push({
        title: 'El Protocolo de la Mañana',
        editionLabel: 'Pack Completo + 4 Bonuses',
        priceNumber: mainPrice,
        priceText: `$${mainPrice % 1 === 0 ? mainPrice : mainPrice.toFixed(2)}`,
        thumbnailUrl: mainThumb
      });
    }
  } catch {}
  return products;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickName(history: string[]): string {
  const available = NAMES.filter(n => !history.includes(n));
  if (available.length === 0) return pickRandom(NAMES);
  return pickRandom(available);
}
function pickProduct(catalog: ScannedProduct[]): ScannedProduct {
  if (catalog.length === 0) {
    // Fallback distribution: 70% pack, 30% digital
    return Math.random() < 0.7 ? FALLBACK_PRODUCTS[0] : FALLBACK_PRODUCTS[1];
  }
  return pickRandom(catalog);
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const catalogRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback(() => {
    const name = pickName(nameHistoryRef.current);
    nameHistoryRef.current = [...nameHistoryRef.current.slice(-9), name];
    const product = pickProduct(catalogRef.current);
    const city = pickRandom(CITIES);
    idRef.current += 1;
    setToast({
      id: idRef.current,
      name,
      city,
      product
    });
    // Auto-hide after 4s
    setTimeout(() => {
      setToast(null);
    }, 4000);
  }, []);
  const scheduleNext = useCallback(() => {
    const delay = 8000 + Math.random() * 7000; // 8-15s
    timeoutRef.current = setTimeout(() => {
      showToast();
      scheduleNext();
    }, delay);
  }, [showToast]);
  useEffect(() => {
    // Scan products on mount (slight delay to let DOM render)
    const scanTimer = setTimeout(() => {
      catalogRef.current = scanPageProducts();
    }, 500);
    // First toast after 3s
    const firstTimer = setTimeout(() => {
      showToast();
      scheduleNext();
    }, 3000);
    return () => {
      clearTimeout(scanTimer);
      clearTimeout(firstTimer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [showToast, scheduleNext]);
  const variants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 80,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 80,
      scale: 0.95
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" data-id="element-121">
      <AnimatePresence mode="wait" data-id="element-122">
        {toast && <motion.div key={toast.id} initial={variants.initial} animate={variants.animate} exit={variants.exit} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 px-4 py-3 flex items-center gap-3 max-w-sm w-[340px] md:w-[380px]" data-id="element-123">
            {toast.product.thumbnailUrl && <img src={toast.product.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" data-id="element-124" />}
            <div className="flex-1 min-w-0" data-id="element-125">
              <p className="text-sm text-charcoal leading-snug" data-id="element-126">
                <span className="font-bold" data-id="element-127">{toast.name}</span>
                <span className="text-charcoal/60" data-id="element-128"> en </span>
                <span className="font-medium" data-id="element-129">{toast.city}</span>
              </p>
              <p className="text-xs text-charcoal/70 mt-0.5 truncate" data-id="element-130">
                compró {toast.product.editionLabel}
              </p>
              <p className="text-xs font-bold text-teal mt-0.5" data-id="element-131">
                {toast.product.priceText} USD
              </p>
            </div>
            <div className="flex-shrink-0" data-id="element-132">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-133"></span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}